/**
 * One aircraft form for Plan and the live "Add to shoot" sheet. Role first (Subject · Chase · Watch),
 * then the registration — a Watch aircraft is "type N-number, tap Add": the label defaults to the
 * registration and the position comes from public ADS-B (hex worked out from a US N-number).
 */
import { useState, type ReactNode } from 'react';
import type { Aircraft, AircraftProviderId, MissionBundle } from '@apos/shared';
import { Button, Field, Segmented, Select, TextInput, Toggle } from '../../components/ui';
import { api, type AircraftInput } from '../../lib/api';
import type { Mutate } from './mutate';
import './setup.css';
import { Disclosure, FormSection, InlineError } from './FormBits';
import { PROVIDERS, useProviders } from './providers';
import { aircraftBody, aircraftPatch, blankAircraft, normalizeRegistration, ROLE_OPTIONS, shootRole, storedRole, validateAircraft, withRole, type ShootRole } from './setupModel';

export interface AircraftFormProps {
  bundle: MissionBundle;
  value: Aircraft | 'new';
  mutate: Mutate;
  /** After save (the saved aircraft) or remove (null). */
  onDone(saved: Aircraft | null): void;
  onCancel(): void;
  render(body: ReactNode, footer: ReactNode): ReactNode;
}

export function AircraftForm({ bundle, value, mutate, onDone, onCancel, render }: AircraftFormProps) {
  const isNew = value === 'new';
  const providers = useProviders(bundle.mission.id);
  const [f, setF] = useState<AircraftInput>(() => (value === 'new' ? blankAircraft(bundle.aircraft) : { ...value, sourceUserId: value.sourceUserId ?? null }));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AircraftInput>(k: K, v: AircraftInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const status = providers.find((p) => p.id === f.provider);
  // which field the one validation message belongs to
  const errAt = !err ? null : /label or registration/.test(err) ? 'label' : /ADS-B|FlightAware/.test(err) ? 'registration' : /phone is on board/.test(err) ? 'phone' : 'form';

  const save = async () => {
    const problem = validateAircraft(f);
    if (problem) return setErr(problem);
    setErr(null);
    const body = aircraftBody(f);
    setBusy(true);
    const r = await mutate(isNew ? 'add the aircraft' : 'save the aircraft', () =>
      value === 'new' ? api.aircraft.create(bundle.mission.id, body) : api.aircraft.update(bundle.mission.id, value.id, aircraftPatch(value, body)),
    );
    setBusy(false);
    if (r) onDone(r);
  };
  const remove = async () => {
    if (value === 'new') return;
    const r = await mutate('remove the aircraft', () => api.aircraft.remove(bundle.mission.id, value.id).then(() => true));
    if (r) onDone(null);
  };

  const body = (
    <div className="setup-form stack-16">
      <FormSection title="Role">
        <Segmented<ShootRole>
          label="Role"
          block
          size="lg"
          value={shootRole(f.role)}
          onChange={(r) => setF((x) => withRole(x, storedRole(r, x.role), isNew))}
          options={ROLE_OPTIONS}
        />
        <p className="setup-hint">
          {f.role === 'subject' ? 'The aircraft you are filming.' : f.role === 'other' ? 'Another aircraft to keep an eye on.' : 'The camera / chase ship.'}
        </p>
      </FormSection>
      <FormSection title="Identity">
        <Segmented<'tracked' | 'labeled'> label="Aircraft identity" className="setup-identity" block value={f.provider === 'manual' && !f.registration ? 'labeled' : 'tracked'} options={[{ value: 'tracked', label: 'Known registration' }, { value: 'labeled', label: 'Label now, identify later' }]} onChange={(v) => { setErr(null); setF((x) => ({ ...x, provider: v === 'labeled' ? 'manual' : 'public', registration: v === 'labeled' ? null : x.registration, icao24: v === 'labeled' ? null : x.icao24, flightAwareIdent: v === 'labeled' ? null : x.flightAwareIdent })); }} />
        <p className="setup-hint">Add each aircraft separately. A label such as Bell 206 does not identify a public track — edit this entry when its registration is confirmed.</p>
        <div className="form-row">
          <Field label="Registration" optional error={errAt === 'registration' ? err : undefined} hint={f.provider === 'public' || f.provider === 'adsb' ? 'A US N-number is resolved automatically.' : undefined}>
            {(id) => (
              <TextInput
                id={id}
                mono
                autoFocus={isNew}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={f.registration ?? ''}
                aria-invalid={errAt === 'registration' || errAt === 'label' ? true : undefined}
                onChange={(e) => { setErr(null); set('registration', normalizeRegistration(e.target.value)); }}
                onKeyDown={(e) => e.key === 'Enter' && void save()}
                placeholder="N555VX"
              />
            )}
          </Field>
          <Field label="Label" optional={f.provider !== 'manual'} error={errAt === 'label' ? err : undefined} hint={f.provider === 'manual' ? 'e.g. Bell 206 / camera ship. No automatic telemetry.' : isNew ? 'Defaults to the registration.' : undefined}>
            {(id) => <TextInput id={id} value={f.label} aria-invalid={errAt === 'label' ? true : undefined} onChange={(e) => { setErr(null); set('label', e.target.value); }} placeholder={f.registration ?? 'Subject 1'} />}
          </Field>
        </div>
        {f.provider === 'public' || f.provider === 'adsb' ? <p className="setup-note">Positions appear when your data source can see the aircraft.</p> : null}
      </FormSection>
      <Disclosure title="Type, tracking source, wingspan" hint={[f.typeDesignator, PROVIDERS.find((p) => p.id === f.provider)?.label.split(' (')[0]].filter(Boolean).join(' · ')}>
        <div className="stack-16">
          <Field label="Type" optional>
            {(id) => <TextInput id={id} value={f.typeDesignator ?? ''} onChange={(e) => set('typeDesignator', e.target.value || null)} placeholder="AS350" />}
          </Field>
          <Field label="Position provider" hint={status?.notes}>
            {(id) => (
              <Select id={id} value={f.provider} onChange={(e) => set('provider', e.target.value as AircraftProviderId)}>
                {PROVIDERS.map((p) => {
                  const st = providers.find((x) => x.id === p.id);
                  return (
                    <option key={p.id} value={p.id}>
                      {p.label}
                      {st && !st.configured ? ' — not set up yet' : ''}
                    </option>
                  );
                })}
              </Select>
            )}
          </Field>
          {f.role==='other'&&(f.provider==='public'||f.provider==='flightaware')&&<>
            <Toggle label="Prioritize tracking" checked={f.trackingPriority==='high'} onChange={v=>set('trackingPriority',v?'high':'normal')}/>
            <p className="setup-note">Check for positions more often when your source and account limits allow. This aircraft stays Watch.</p>
          </>}
          {f.provider === 'crew_device' ? (
            <Field label="Phone on board" error={errAt === 'phone' ? err : undefined} hint="That member's shared location becomes this aircraft's position (e.g. the chase ship).">
              {(id) => (
                <Select id={id} value={f.sourceUserId ?? ''} onChange={(e) => { setErr(null); set('sourceUserId', e.target.value || null); }}>
                  <option value="">Choose a crew member…</option>
                  {bundle.members
                    .filter((m) => !m.isSimulated)
                    .map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.callsign || m.displayName}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          ) : null}
          {f.provider === 'public' ? (
            <p className="setup-note">
              Positions come from the shoot owner’s aircraft data: Airview Studio’s free public feeds on accounts that have them, otherwise the owner’s own
              enabled providers (My settings), in priority order. Without an enabled source, nothing is tracked. Test aircraft may not be visible at all; the app will say so rather than guess.
            </p>
          ) : null}
          {f.provider === 'adsb' || f.provider === 'public' ? (
            <Field label="ICAO24 (hex)" optional hint="Worked out from a US registration automatically">
              {(id) => (
                <TextInput id={id} mono value={f.icao24 ?? ''} onChange={(e) => set('icao24', e.target.value.replace(/[^0-9a-f]/gi, '').slice(0, 6) || null)} placeholder="a9c1f2" />
              )}
            </Field>
          ) : null}
          {f.provider === 'flightaware' ? (
            <Field label="FlightAware ident" hint="Registration or flight ident">
              {(id) => <TextInput id={id} mono value={f.flightAwareIdent ?? ''} onChange={(e) => set('flightAwareIdent', e.target.value.toUpperCase() || null)} />}
            </Field>
          ) : null}
          <Field label="Wingspan (m)" optional hint="For frame-fill estimates">
            {(id) => (
              <TextInput
                id={id}
                mono
                inputMode="decimal"
                value={f.wingspanM ?? ''}
                onChange={(e) => set('wingspanM', e.target.value ? Number(e.target.value.replace(/[^\d.]/g, '')) || null : null)}
              />
            )}
          </Field>
        </div>
      </Disclosure>
      {errAt === 'form' && err ? <InlineError>{err}</InlineError> : null}
    </div>
  );

  const footer = (
    <div className="setup-foot">
      {!isNew ? (
        <Button variant="danger" icon="trash" onClick={() => void remove()}>
          Remove
        </Button>
      ) : null}
      <span className="setup-foot__spacer" />
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={() => void save()} loading={busy}>
        {isNew ? 'Add aircraft' : 'Save aircraft'}
      </Button>
    </div>
  );

  return <>{render(body, footer)}</>;
}
